import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Avatar, AgGridTable, Button, Pagination, DateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt, MdOutlineFactCheck, MdPieChart, MdViewKanban, MdFormatListBulleted, MdStorefront } from 'react-icons/md';
import { getWaivers, getWaiverStatusCounts, exportData } from 'redux/crm/project/waiverSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import NumberRenderer from 'components/common/ag-grid/NumberRenderer';
import DateFormatRenderer from 'components/common/ag-grid/DateFormatRenderer';
import { PersonRenderer, iconCell } from 'components/common/ag-grid';
import JobFormatRenderer from 'components/app/crm/reports/components/JobFormatRenderer';
import WaiverKanbanBoard from './WaiverKanbanBoard';
import { debounce } from 'helpers/debounce';
import { formatNumber } from 'helpers/utils';

// ── Constants ─────────────────────────────────────────────────────────────────

// waiver_no matches the on-screen "WAI01234" format server-side, so the quick
// search accepts a waiver number, quote number, job number or project name.
const SEARCH_FIELD = 'waiver_no_or_project_name_or_project_q_number_or_project_j_number_cont';

const THIS_MONTH_START = dayjs().startOf('month').format('YYYY-MM-DD');
const THIS_MONTH_END   = dayjs().format('YYYY-MM-DD');

// Quick date filters — three independent ranges; created date is on by default
const DATE_RANGE_FILTERS = [
  { key: 'created',     label: 'Created Date', paramKeys: ['created_at_gteq', 'created_at_lteq'] },
  { key: 'sales_entry', label: 'Sales Entry',  paramKeys: ['sales_entry_date_gteq', 'sales_entry_date_lteq'] },
  { key: 'commitment',  label: 'Commitment',   paramKeys: ['commitment_date_gteq', 'commitment_date_lteq'] },
];

const EMPTY_RANGE = { startDate: '', endDate: '' };

const STATUS_BADGE = {
  1: { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' }, // Submitted
  2: { bg: '#fffbeb', border: '#fbbf24', text: '#b45309' }, // Waiting for approval
  3: { bg: '#dcfce7', border: '#4ade80', text: '#15803d' }, // Approved
  4: { bg: '#fee2e2', border: '#f87171', text: '#b91c1c' }, // Rejected
  5: { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' }, // Canceled
};

const STATUS_OPTIONS = [
  { value: 1, label: 'Submitted' },
  { value: 2, label: 'Waiting for approval' },
  { value: 3, label: 'Approved' },
  { value: 4, label: 'Rejected' },
  { value: 5, label: 'Canceled' },
];

const StatusBadge = ({ data }) => {
  const status = data?.status;
  if (!status?.name) return <span className="tw-text-slate-300">—</span>;
  const s = STATUS_BADGE[status.id] ?? STATUS_BADGE[5];
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1">
      <span
        style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
        className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-leading-4"
      >
        {status.name}
      </span>
      {data?.close?.closed && <span className="tw-text-[10px] tw-leading-none tw-text-gray-400">closed</span>}
    </span>
  );
};
StatusBadge.propTypes = { data: PropTypes.object };

const HtmlCell = ({ value }) => (
  value ? <span className="tw-text-slate-600" dangerouslySetInnerHTML={{ __html: value }} /> : null
);
HtmlCell.propTypes = { value: PropTypes.string };

// ── Column definitions (colId = ransack sort attribute) ───────────────────────

const COLUMN_DEFS = [
  {
    headerName: 'ID#',
    colId: 'id',
    // Pinned columns must opt out of the grid-wide flex sizing
    width: 120, minWidth: 100, flex: 0, pinned: 'left',
    valueGetter: (p) => p.data?.waiver_number,
    cellRenderer: (p) => {
      const { id, project } = p.data || {};
      if (!project?.id) return <span className="tw-font-mono">{p.value}</span>;
      return (
        <Link to={`/crm/projects/${project.id}/waivers/${id}`} state={{ source_waiver: true }}
          className="tw-font-mono tw-font-semibold tw-text-blue-700 hover:tw-underline">
          {p.value}
        </Link>
      );
    },
  },
  {
    headerName: 'Quote Number', colId: 'project_q_number', minWidth: 130,
    valueGetter: (p) => p.data?.project?.q_number,
    cellRenderer: (p) => {
      const proj = p.data?.project;
      if (!proj?.id) return p.value || null;
      return <Link to={`/crm/projects/${proj.id}`} className="tw-text-yellow-600 hover:tw-underline tw-font-medium">{p.value}</Link>;
    },
  },
  {
    headerName: 'P/J Number', colId: 'project_j_number', minWidth: 120,
    valueGetter: (p) => p.data?.project?.j_number,
    cellRenderer: JobFormatRenderer,
  },
  {
    headerName: 'Project Name', colId: 'project_name', minWidth: 200, tooltipValueGetter: (p) => p.value,
    valueGetter: (p) => p.data?.project?.name,
    cellRenderer: (p) => {
      const proj = p.data?.project;
      if (!proj?.id) return p.value || null;
      return <Link to={`/crm/projects/${proj.id}`} className="tw-text-blue-600 hover:tw-underline">{p.value}</Link>;
    },
  },
  {
    headerName: 'Customer', colId: 'customer_name', minWidth: 180, sortable: false,
    valueGetter: (p) => p.data?.project?.customer?.name,
    cellRenderer: (p) => {
      const customer = p.data?.project?.customer;
      if (!customer?.name) return p.value || null;
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-max-w-full">
          <Avatar name={customer.name} alt={customer.name} size="xs" className="tw-shrink-0" />
          {customer.id
            ? <Link to={`/crm/customers/${customer.id}`} className="hover:tw-underline tw-truncate">{p.value}</Link>
            : <span className="tw-truncate">{p.value}</span>}
        </span>
      );
    },
  },
  {
    headerName: 'Status', colId: 'status_approval_id', minWidth: 165,
    valueGetter: (p) => p.data?.status?.name,
    cellRenderer: StatusBadge,
  },
  { headerName: 'Description', field: 'description', minWidth: 220, sortable: false, cellRenderer: HtmlCell },
  {
    headerName: 'Value', colId: 'waiver_value', minWidth: 120,
    valueGetter: (p) => p.data?.waiver_value,
    cellRenderer: (p) => <span className="tw-tabular-nums tw-text-emerald-700">${formatNumber(p.value)}</span>,
  },
  { headerName: 'Weight (M.T)',     colId: 'waiver_weight',    minWidth: 120, valueGetter: (p) => p.data?.waiver_weight, cellRenderer: NumberRenderer },
  { headerName: 'Sales Entry',      colId: 'sales_entry_date', minWidth: 120, valueGetter: (p) => p.data?.sales_entry_date, cellRenderer: DateFormatRenderer },
  { headerName: 'Commitment',       colId: 'commitment_date',  minWidth: 120, valueGetter: (p) => p.data?.commitment_date,  cellRenderer: DateFormatRenderer },
  // created_date arrives pre-formatted from the serializer; sort still maps to created_at
  { headerName: 'Created Date',     colId: 'created_at',       minWidth: 150, valueGetter: (p) => p.data?.created_date },
  {
    headerName: 'CSE Incharge', field: 'cse_incharge', minWidth: 140, sortable: false,
    valueGetter: (p) => (Array.isArray(p.data?.cse_incharge) ? p.data.cse_incharge.join(', ') : ''),
  },
  { headerName: 'Sales Incharge', colId: 'project_sales_incharge', minWidth: 130, valueGetter: (p) => p.data?.project?.sales_incharge, cellRenderer: PersonRenderer },
  { headerName: 'Sales Unit',     colId: 'sales_unit_id',          minWidth: 130, valueGetter: (p) => p.data?.sales_unit?.description, cellRenderer: iconCell(MdStorefront) },
  { headerName: 'Requestor',      field: 'requestor',              minWidth: 110, sortable: false, cellRenderer: PersonRenderer },
  { headerName: 'Approved By',    colId: 'approved_by',            minWidth: 110, sortable: false, valueGetter: (p) => p.data?.approved?.by, cellRenderer: PersonRenderer },
  { headerName: 'Approved On',    colId: 'approved_on',            minWidth: 120, sortable: false, valueGetter: (p) => p.data?.approved?.date, cellRenderer: DateFormatRenderer },
  { headerName: 'Closed By',      colId: 'closed_by',              minWidth: 110, sortable: false, valueGetter: (p) => p.data?.close?.by, cellRenderer: PersonRenderer },
  { headerName: 'Closed Date',    colId: 'closed_date',            minWidth: 120, sortable: false, valueGetter: (p) => p.data?.close?.date, cellRenderer: DateFormatRenderer },
  { headerName: 'Comment',        field: 'comment',                minWidth: 220, sortable: false },
  { headerName: 'CSD Remark',     field: 'csd_remark',             minWidth: 220, sortable: false, cellRenderer: HtmlCell },
];

// ── Advanced filter drawer fields (same criteria as the legacy modal) ─────────

const buildFilterFields = (unitOptions) => [
  { key: 'id',            label: 'Waiver Number',  type: 'text', paramKey: 'id_eq',                       tagName: 'ID#'     },
  { key: 'q_number',      label: 'Quote Number',   type: 'text', paramKey: 'project_q_number_cont',       tagName: 'Q#'      },
  { key: 'j_number',      label: 'P/J Number',     type: 'text', paramKey: 'project_j_number_cont',       tagName: 'J#'      },
  { key: 'project_name',  label: 'Project Name',   type: 'text', paramKey: 'project_name_cont',           tagName: 'Project' },
  {
    key: 'sales_entry_date', label: 'Sales Entry Date', type: 'date_range_picker',
    paramKeys: ['sales_entry_date_gteq', 'sales_entry_date_lteq'], tagName: 'Entry',
  },
  {
    key: 'commitment_date', label: 'Commitment Date', type: 'date_range_picker',
    paramKeys: ['commitment_date_gteq', 'commitment_date_lteq'], tagName: 'Commitment',
  },
  { key: 'closed',        label: 'Closed of waiver', type: 'checkbox', paramKey: 'closed_true',           tagName: 'Closed'  },
  { key: 'sales_incharge',label: 'Sales Incharge',  type: 'text', paramKey: 'project_sales_incharge_cont',tagName: 'Sales'   },
  { key: 'cse_incharge',  label: 'CSE Incharge',    type: 'text', paramKey: 'cse_incharge_cont',          tagName: 'CSE'     },
  { key: 'statuses',      label: 'Status',          type: 'multiselect', options: STATUS_OPTIONS, paramKey: 'status_approval_id_in', tagName: 'Status' },
  { key: 'sales_units',   label: 'Sales Unit',      type: 'multiselect', options: unitOptions,   paramKey: 'sales_unit_id_in',      tagName: 'Unit'   },
  { key: 'created_by',    label: 'Created By',      type: 'text', paramKey: 'user_name_cont',             tagName: 'Created By' },
  {
    key: 'created_date', label: 'Created Date', type: 'date_range_picker',
    paramKeys: ['created_at_gteq', 'created_at_lteq'], tagName: 'Created',
  },
];

const DIVIDER_AFTER = ['project_name', 'closed', 'cse_incharge'];

// ── Component ─────────────────────────────────────────────────────────────────

const WaiverListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allData, statusCounts, exporting } = useSelector((s) => s.crm_waiver);
  const { data = [], totalRows = 0 } = allData || {};
  const counts = statusCounts?.data;
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [view, setView]                 = useState('list');
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(50);
  const [sort, setSort]                 = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]             = useState('');
  const [dateRanges, setDateRanges]     = useState({
    created:     { startDate: THIS_MONTH_START, endDate: THIS_MONTH_END },
    sales_entry: EMPTY_RANGE,
    commitment:  EMPTY_RANGE,
  });
  const [unitIds, setUnitIds]           = useState([]);
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(buildFilterFields([])));
  const [filterTags, setFilterTags]     = useState([]);
  const [showFilter, setShowFilter]     = useState(false);
  const [loading, setLoading]           = useState(false);

  // Always-fresh refs (handlers compose params without stale closures)
  const searchRef       = useRef('');
  const dateRangesRef   = useRef(dateRanges);
  const unitsRef        = useRef([]);
  const filterParamsRef = useRef({});
  const sortRef         = useRef(sort);
  const pageRef         = useRef(page);
  const perPageRef      = useRef(perPage);

  dateRangesRef.current = dateRanges;
  unitsRef.current     = unitIds;
  sortRef.current      = sort;
  pageRef.current      = page;
  perPageRef.current   = perPage;

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const filterFields = useMemo(() => buildFilterFields(
    activeSalesUnitsData.map((u) => ({ value: u.id, label: u.description || u.name }))
  ), [activeSalesUnitsData]);

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  // ── Param builders ────────────────────────────────────────────────

  const buildDateParams = useCallback((ranges) => {
    const p = {};
    DATE_RANGE_FILTERS.forEach((f) => {
      const range = ranges[f.key] || EMPTY_RANGE;
      if (range.startDate) p[f.paramKeys[0]] = range.startDate;
      if (range.endDate)   p[f.paramKeys[1]] = range.endDate;
    });
    return p;
  }, []);

  // Shared criteria (search / dates / units / drawer) — used by both the
  // list fetch and the stage counts fetch so chips always match the grid.
  const buildCriteria = useCallback((overrides = {}) => ({
    ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
    ...buildDateParams(dateRangesRef.current),
    ...(unitsRef.current.length ? { sales_unit_id_in: unitsRef.current } : {}),
    ...filterParamsRef.current,
    ...overrides,
  }), [buildDateParams]);

  const buildListParams = useCallback((overrides = {}) => ({
    page:     pageRef.current,
    per_page: perPageRef.current,
    sort:     sortRef.current,
    ...buildCriteria(),
    ...overrides,
  }), [buildCriteria]);

  // ── Fetch ─────────────────────────────────────────────────────────

  // Bumped whenever search/date/unit/drawer criteria change — drives the
  // kanban board refetch without exposing the refs as dependencies.
  const [criteriaTick, setCriteriaTick] = useState(0);
  const kanbanCriteria = useMemo(
    () => buildCriteria(),
    // criteriaTick is the recompute trigger: buildCriteria reads refs, which do not re-render on their own
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [criteriaTick, buildCriteria]
  );

  const fetchList = useCallback(async (params) => {
    setLoading(true);
    try { await dispatch(getWaivers(params)); } finally { setLoading(false); }
  }, [dispatch]);

  // Counts ignore the stage itself — they refresh only when criteria change
  const fetchCounts = useCallback((criteriaOverrides = {}) => {
    dispatch(getWaiverStatusCounts(buildCriteria(criteriaOverrides)));
  }, [dispatch, buildCriteria]);

  useEffect(() => {
    fetchList(buildListParams({ page, per_page: perPage, sort }));
  }, [fetchList, buildListParams, page, perPage, sort]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const debouncedFetch = useRef(debounce((listParams, countParams) => {
    fetchList(listParams);
    dispatch(getWaiverStatusCounts(countParams));
    setCriteriaTick((t) => t + 1);
  }, 500)).current;

  // ── Handlers ──────────────────────────────────────────────────────

  const refreshAll = (listOverrides = {}) => {
    setPage(1);
    fetchList(buildListParams({ page: 1, ...listOverrides }));
    fetchCounts();
    setCriteriaTick((t) => t + 1);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch(buildListParams({ page: 1 }), buildCriteria());
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    refreshAll();
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    sortRef.current = newSort;
    setSort(newSort);
    setPage(1);
    fetchList(buildListParams({ page: 1, sort: newSort }));
  }, [fetchList, buildListParams]);

  const handleDateRangeChange = (key) => (range) => {
    const next = { ...dateRangesRef.current, [key]: range };
    dateRangesRef.current = next;
    setDateRanges(next);
    refreshAll();
  };

  const handleUnitChange = (ids) => {
    unitsRef.current = ids;
    setUnitIds(ids);
    refreshAll();
  };

  const handleApplyFilter = (params, tags) => {
    filterParamsRef.current = params;
    setFilterTags(tags);
    setShowFilter(false);
    refreshAll();
  };

  const handleResetFilter = () => {
    filterParamsRef.current = {};
    setFilterValues(getEmptyValues(filterFields));
    setFilterTags([]);
    refreshAll();
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(
      Object.entries(filterParamsRef.current).filter(([k]) => k !== tag.id)
    );
    filterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    refreshAll();
  };

  const handleExport = () => {
    dispatch(exportData({ ...buildListParams(), export_status: 'all' }));
  };

  const activeFilterCount = filterTags.length;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
          <div className="tw-flex tw-items-center tw-gap-2">
            <MdOutlineFactCheck className="tw-text-blue-600" size={22} />
            <div>
              <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Waivers</h1>
              <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Track waiver requests across every approval stage.</p>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            variant="secondary"
            size="md"
            leftIcon={MdPieChart}
            onClick={() => navigate('/dashboard/crm/waiver')}
            className="tw-bg-primary-50 tw-text-primary-700 tw-border-primary-300 hover:tw-bg-primary-100"
          >
            Dashboard
          </Button>
          <Button
            variant="secondary"
            size="md"
            leftIcon={FaSyncAlt}
            loading={loading}
            onClick={() => refreshAll()}
          >
            Reload
          </Button>
          <Button
            variant="secondary"
            size="md"
            leftIcon={FaFileExcel}
            loading={exporting}
            disabled={loading}
            onClick={handleExport}
            className="tw-bg-green-50 tw-text-green-700 tw-border-green-300 hover:tw-bg-green-100"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Quick search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-52 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="WAI#, Q#, J#, project name..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Three independent date filters (created date is preset to this month) */}
          {DATE_RANGE_FILTERS.map((f) => (
            <DateRangePicker
              key={f.key}
              label={f.label}
              startDate={dateRanges[f.key].startDate}
              endDate={dateRanges[f.key].endDate}
              onChange={handleDateRangeChange(f.key)}
            />
          ))}

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Sales Unit */}
          <SalesUnitGroupedDropdown
            units={activeSalesUnitsData}
            selectedIds={unitIds}
            onChange={handleUnitChange}
            placeholder="All Units"
            width={220}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* More Filters */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              activeFilterCount > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {activeFilterCount}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active filter tags (drawer only) */}
        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : String(tag.value)}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <Button
              variant="secondary"
              size="xs"
              leftIcon={FaTimes}
              onClick={handleResetFilter}
              className="tw-bg-transparent tw-text-red-500 tw-border-red-200 hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500"
            >
              Clear all
            </Button>
          </div>
        )}
      </div>


      {/* View tabs: flat list vs status kanban board */}
      <div className="tw-flex tw-items-center tw-gap-1 tw-border-b tw-border-slate-200">
        {[
          { key: 'list',   label: 'All',    Icon: MdFormatListBulleted },
          { key: 'kanban', label: 'Kanban', Icon: MdViewKanban },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-border-0 tw-border-b-2 tw-bg-transparent tw-cursor-pointer tw-transition-colors -tw-mb-px ${
              view === key
                ? 'tw-border-primary-600 tw-text-primary-700'
                : 'tw-border-transparent tw-text-slate-500 hover:tw-text-slate-700'
            }`}
            style={{ borderBottomStyle: 'solid' }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {view === 'list' ? (
        <>
          {/* Grid */}
          <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
            <AgGridTable
              rowData={data}
              columnDefs={COLUMN_DEFS}
              loading={loading}
              pagination={false}
              filter={false}
              height="max(320px, calc(100dvh - 340px))"
              onSortChanged={handleSort}
              enableCellTextSelection
            />
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            pageSizeOptions={[20, 50, 100, 200]}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
            disabled={loading}
          />
        </>
      ) : (
        <WaiverKanbanBoard criteria={kanbanCriteria} counts={counts} />
      )}

      {/* Advanced filter drawer — same criteria set as the legacy modal */}
      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={filterFields}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Apply filters to advances"
        description="Establish the waiver's filtering criteria"
        dividerAfter={DIVIDER_AFTER}
      />
    </div>
  );
};

export default WaiverListPage;
